// Shared route-point (POI) icon/label/color registry — Sprint 09B. Used by
// RouteBuilderModal.jsx here (admin "Punten" editor) and by the equivalent copy in
// companion-app/src/lib/routePointIcons.js (participant Navigation.jsx markers) —
// separate apps/bundlers, so this file is intentionally duplicated rather than
// shared via a package, same convention as mapStyle.js. Kept in sync with the backend's own
// whitelist in backend/server.py (ROUTE_POINT_TYPES / ROUTE_POINT_ICON_KEYS /
// ROUTE_POINT_DEFAULT_ICON / ROUTE_POINT_DEFAULT_APPROACH_M / ...) — if either side
// adds a type or icon, update the other.
//
// Icons are bundled with the app (a fixed import list below), never resolved from an
// arbitrary string, so they render correctly offline — no icon ever fails to load
// because of a missing network request.
import { Play, Flag, HeartPulse, PackageOpen, Droplets, Toilet, CircleCheck, Info, TriangleAlert, CupSoda, Camera, ParkingCircle, MapPin, Sparkles } from "lucide-react";

// Mirrors backend ROUTE_POINT_TYPES exactly.
export const ROUTE_POINT_TYPES = [
  "start", "finish", "first_aid", "distribution", "water", "toilet",
  "checkpoint", "info", "warning", "refreshment", "photo", "parking",
  // Sprint 09K — a scavenger-hunt search point.
  "hunt",
];

// Fixed icon_key -> component mapping. Never render a component looked up by an
// arbitrary/user-supplied string — only these keys are ever resolved (see
// getRoutePointIconComponent's MapPin fallback for anything else).
export const ROUTE_POINT_ICON_COMPONENTS = {
  play: Play,
  flag: Flag,
  "heart-pulse": HeartPulse,
  "package-open": PackageOpen,
  droplets: Droplets,
  toilet: Toilet,
  "circle-check": CircleCheck,
  info: Info,
  "triangle-alert": TriangleAlert,
  "cup-soda": CupSoda,
  camera: Camera,
  "parking-circle": ParkingCircle,
  "map-pin": MapPin,
  sparkles: Sparkles,
};

export const ROUTE_POINT_ICON_KEYS = Object.keys(ROUTE_POINT_ICON_COMPONENTS);

export const ROUTE_POINT_DEFAULT_ICON = {
  start: "play",
  finish: "flag",
  first_aid: "heart-pulse",
  distribution: "package-open",
  water: "droplets",
  toilet: "toilet",
  checkpoint: "circle-check",
  info: "info",
  warning: "triangle-alert",
  refreshment: "cup-soda",
  photo: "camera",
  parking: "parking-circle",
  hunt: "sparkles",
};

// Dutch label + marker fill color per point_type — shared by the admin builder and
// the participant navigation map so both stay visually consistent. Raw hex (not
// Tailwind a4d-* tokens) to match the existing marker-color convention already used
// in RouteBuilderModal.jsx / Navigation.jsx (MapLibre markers take a plain color
// string, not a CSS class).
export const ROUTE_POINT_META = {
  start: { label: "Start", color: "#16a34a" },
  finish: { label: "Finish", color: "#dc2626" },
  first_aid: { label: "EHBO", color: "#dc2626" },
  distribution: { label: "Uitdeelpunt", color: "#a855f7" },
  water: { label: "Water", color: "#0ea5e9" },
  toilet: { label: "Toilet", color: "#64748b" },
  checkpoint: { label: "Checkpoint", color: "#16a34a" },
  info: { label: "Info", color: "#3b82f6" },
  warning: { label: "Let op", color: "#f59e0b" },
  refreshment: { label: "Versnapering", color: "#f97316" },
  photo: { label: "Foto", color: "#ec4899" },
  parking: { label: "Parkeren", color: "#64748b" },
  // Teal: distinct from the green of start/checkpoint and the sky blue of water, so
  // a search point never reads as one of the practical points at a glance.
  hunt: { label: "Zoekpunt", color: "#14b8a6" },
};

// Engineering-default notification distances (meters) — mirrors backend
// ROUTE_POINT_DEFAULT_APPROACH_M / ROUTE_POINT_DEFAULT_ARRIVAL_M / ROUTE_POINT_
// FALLBACK_*. The participant navigation endpoint always resolves a concrete number
// server-side (never null), so Navigation.jsx never needs these directly — exported
// for the admin editor panel, which shows the effective default before an admin
// overrides it.
export const ROUTE_POINT_DEFAULT_APPROACH_M = { finish: 150, warning: 100 };
export const ROUTE_POINT_DEFAULT_ARRIVAL_M = { finish: 30, warning: 30 };
export const ROUTE_POINT_FALLBACK_APPROACH_M = 75;
export const ROUTE_POINT_FALLBACK_ARRIVAL_M = 25;

export function getRoutePointDefaultApproachMeters(pointType) {
  return ROUTE_POINT_DEFAULT_APPROACH_M[pointType] ?? ROUTE_POINT_FALLBACK_APPROACH_M;
}

export function getRoutePointDefaultArrivalMeters(pointType) {
  return ROUTE_POINT_DEFAULT_ARRIVAL_M[pointType] ?? ROUTE_POINT_FALLBACK_ARRIVAL_M;
}

export function getRoutePointIconComponent(iconKey) {
  return ROUTE_POINT_ICON_COMPONENTS[iconKey] || MapPin;
}

export function getRoutePointMeta(pointType) {
  return ROUTE_POINT_META[pointType] || { label: pointType, color: "#64748b" };
}
